import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, initials, timeAgo, showToast } from "@/lib/api";
import { useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import { expBadge, rolePill, ShareStoryModal, StartTopicModal } from "@/components/Modals";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [p, setP] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [postAs, setPostAs] = useState("real");
  const [shareOpen, setShareOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/posts/${id}`),
        api.get(`/posts/${id}/comments`)
      ]);
      setP(r1.data);
      setComments(r2.data || []);
    } catch (e) {
      showToast("Post not found");
      navigate("/");
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const reply = async () => {
    if (!user) { login(); return; }
    if (!body.trim()) return;
    try {
      await api.post(`/posts/${id}/comments`, { body: body.trim(), post_as: postAs });
      showToast("Reply submitted for review");
      setBody("");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed");
    }
  };

  const like = async () => {
    if (!user) { login(); return; }
    try {
      const r = await api.post(`/posts/${id}/like`);
      setP({...p, likes: (p.likes||0) + (r.data.liked ? 1 : -1)});
    } catch {}
  };

  if (!p) return (
    <div><Navbar onShare={()=>setShareOpen(true)} onTopic={()=>setTopicOpen(true)} /></div>
  );

  const eb = expBadge(p.experience);
  const rp = rolePill(p.author_role, p.is_anonymous);

  return (
    <div>
      <Navbar onShare={()=>{ if(!user){login();return;} setShareOpen(true); }} onTopic={()=>{ if(!user){login();return;} setTopicOpen(true); }} />
      <div className="detail-outer">
        <button className="back" data-testid="back-to-forum" onClick={()=>navigate("/")}>← Back to Forum</button>

        <div className="detail-card">
          <div className="detail-cats">
            {eb && <span className={`dcard-exp-badge ${eb.cls}`} style={{position:"static"}}>{eb.label}</span>}
            <span className="dtag city">{p.city}</span>
            {p.topic_chip && <span className="topic-chip">{p.topic_chip}</span>}
            {(p.tags||[]).map((t,i)=> <span key={i} className="dtag">{t}</span>)}
          </div>
          <h1 className="detail-title" data-testid="detail-title">{p.title}</h1>

          <div className="detail-author-row">
            <div className="da-av" style={{background:p.is_anonymous?"#F0EEE9":"#EBE8F7", color:p.is_anonymous?"#6C6C70":"#6050C8"}}>
              {p.display_initials}
            </div>
            <div className="da-info">
              <h5>{p.display_author} {rp && <span className={`rpill ${rp.cls}`} style={{marginLeft:6}}>{rp.label}</span>}</h5>
              <p>📍 {p.city}{p.area?`, ${p.area}`:""} · {timeAgo(p.created_at)}</p>
            </div>
          </div>

          <div className="detail-body">{p.body}</div>

          {(p.images||[]).length > 0 && (
            <div className="detail-imgs">
              {p.images.map(path => (
                <img key={path} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`} alt="" />
              ))}
            </div>
          )}

          <div className="detail-footer">
            <button className="daction" data-testid="detail-like" onClick={like}>👍 {p.likes||0}</button>
            <span className="daction">💬 {comments.length}</span>
            <span className="daction">👁 {p.views||0}</span>
            <button className="daction" data-testid="detail-flag" style={{marginLeft:"auto"}} onClick={async()=>{ if(!user){login();return;} await api.post(`/posts/${id}/flag`,{reason:""}); showToast("Reported. Thanks."); }}>🚩 Report</button>
          </div>
        </div>

        <div className="cmts-card">
          <div className="cmts-heading">Conversation ({comments.length})</div>
          {comments.length === 0 && <p style={{color:"#6C6C70",fontSize:13.5,marginBottom:18}}>Be the first to add a thoughtful reply.</p>}
          {comments.map(c => (
            <div key={c.id} className="cmt">
              <div className="cmt-head">
                <div className="cmt-av">{c.display_initials || initials(c.display_author)}</div>
                <span className="cmt-author">{c.display_author}</span>
                <span className="cmt-time">{timeAgo(c.created_at)}</span>
              </div>
              <div className="cmt-body">{c.body}</div>
            </div>
          ))}

          <div className="reply-box">
            <div className="reply-lbl">Add a reply</div>
            <div className="name-toggle-row" style={{marginBottom:8}}>
              <button data-testid="reply-as-real" className={`ntr-btn ${postAs==="real"?"on":""}`} onClick={()=>setPostAs("real")}>Real Name</button>
              <button data-testid="reply-as-username" className={`ntr-btn ${postAs==="username"?"on":""}`} onClick={()=>setPostAs("username")}>Username</button>
              <button data-testid="reply-as-anon" className={`ntr-btn ${postAs==="anonymous"?"on":""}`} onClick={()=>setPostAs("anonymous")}>Anonymous</button>
            </div>
            <textarea data-testid="reply-textarea" className="reply-ta" placeholder="Share your thoughts thoughtfully…" value={body} onChange={(e)=>setBody(e.target.value)} />
            <div className="reply-note">⏳ Replies are reviewed before going live.</div>
            <div className="reply-footer">
              <span style={{fontSize:12,color:"#6C6C70"}}>Posting as {postAs==="real"?(user?.real_name||user?.name||"You"):postAs==="username"?(user?.username||"username"):"Anonymous"}</span>
              <button className="btn-dark" data-testid="reply-submit" onClick={reply}>Submit Reply →</button>
            </div>
          </div>
        </div>
      </div>

      <ShareStoryModal open={shareOpen} onClose={()=>setShareOpen(false)} />
      <StartTopicModal open={topicOpen} onClose={()=>setTopicOpen(false)} />
    </div>
  );
}
